import Close from "@mui/icons-material/Close";
import CopyAllOutlined from "@mui/icons-material/CopyAllOutlined";
import { Alert, AlertColor, IconButton, Snackbar } from "@mui/material";
import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type AlertOptions = {
  /** Duration in milliseconds to automatically hide the alert. When
   * `undefined`, 10,000ms is used. When `null`, the alert will not
   * automatically hide. */
  autoHideDuration?: number | null;
};

type AlertContext = {
  success: (msg: ReactNode, options?: AlertOptions) => void;
  error: (msg: ReactNode, error?: any, options?: AlertOptions) => void;
  info: (msg: ReactNode, options?: AlertOptions) => void;
};

export const ctx = createContext<AlertContext>({
  success: () => {},
  error: () => {},
  info: () => {},
});

export function AlertProvider(props: PropsWithChildren<Record<string, any>>) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<ReactNode>();
  const [err, setError] = useState<Error | undefined>();
  const [severity, setSeverity] = useState<AlertColor | undefined>();
  const [options, setOptions] = useState<AlertOptions | undefined>();

  const success = useCallback((msg: ReactNode, options?: AlertOptions) => {
    setSeverity("success");
    setMsg(msg);
    setError(undefined);
    setOptions(options);
    setOpen(true);
  }, []);

  const error = useCallback(
    (msg: ReactNode, error?: any, options?: AlertOptions) => {
      setSeverity("error");
      setMsg(msg);
      setError(error);
      setOptions(options);
      setOpen(true);
    },
    []
  );

  const info = useCallback((msg: ReactNode, options?: AlertOptions) => {
    setSeverity("info");
    setMsg(msg);
    setError(undefined);
    setOptions(options);
    setOpen(true);
  }, []);

  const state = useMemo(
    () => ({
      success,
      error,
      info,
    }),
    [error, info, success]
  );

  // Default to 10,000 ms if options.autoHideDuration is not provided or
  // undefined. null means no auto hide.
  const autoHideDuration =
    options?.autoHideDuration === undefined ? 10000 : options.autoHideDuration;
  return (
    <ctx.Provider value={state}>
      {props.children}
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={(e, reason) => {
          if (reason === "clickaway") return;
          setOpen(false);
        }}
      >
        <Alert
          severity={severity}
          onClose={() => setOpen(false)}
          action={
            <>
              {err && (
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(err.stack ?? "");
                  }}
                >
                  <CopyAllOutlined fontSize="inherit" />
                </IconButton>
              )}
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setOpen(false);
                }}
              >
                <Close fontSize="inherit" />
              </IconButton>
            </>
          }
          sx={{
            whiteSpace: "pre-line",
          }}
        >
          {msg} {err && err.message}
        </Alert>
      </Snackbar>
    </ctx.Provider>
  );
}

export function useAlerts() {
  return useContext(ctx);
}
